import React, { useMemo, useState } from 'react';
import { roiMultiple } from '../utils/roiMultiple';
import { CalculationResult, Timeframe, activeRepsForRow, DOUBLE_ATTRITION_SURVIVAL, YEAR_ONE_MONTHS, Inputs } from '../utils/roiCalculations';
import { fmtMoney, fmtPct } from '../utils/formatters';
import { yearOneHeadline } from '../utils/yearOneHeadline';
import { SummerPestHeadline } from './SummerPestHeadline';
interface ResultsSummaryProps {
  results: CalculationResult;
  halfSales?: boolean;
  doubleAttrition?: boolean;
  timeframe?: Timeframe;
  onOptimizeForTarget?: (target: number, months: Timeframe) => void;
  onOptimizeForTeamTarget?: (target: number, months: Timeframe) => void;
  onUndoOptimize?: () => void;
  canUndoOptimize?: boolean;
  // Pest: years two and three are a year-one customer trail.
  revenueTrail?: boolean;
  // Summer Pest swaps in operator-language headline cards.
  inputs?: Inputs;
}
export function ResultsSummary({
  results,
  halfSales = false,
  doubleAttrition = false,
  timeframe = 12,
  onOptimizeForTarget,
  onOptimizeForTeamTarget,
  onUndoOptimize,
  canUndoOptimize = false,
  revenueTrail = false,
  inputs
}: ResultsSummaryProps) {
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetValue, setTargetValue] = useState('');
  const [editingTeamTarget, setEditingTeamTarget] = useState(false);
  const [teamTargetValue, setTeamTargetValue] = useState('');
  const calculatedTotals = useMemo(() => {
    const lastRow = results.rows[timeframe];
    if (!lastRow) {
      return {
        totalGrossRevenue: 0,
        totalGrossProfit: 0,
        totalNetProfit: 0,
        roi: 0
      };
    }
    let cumGrossRevenue = 0;
    let cumGrossProfit = 0;
    let cumTotalCost = 0;
    for (let i = 0; i <= timeframe; i++) {
      cumGrossRevenue += results.rows[i].grossRevenue;
      cumGrossProfit += results.rows[i].grossProfit;
      cumTotalCost += results.rows[i].totalCost;
      // Doubled attrition: fewer surviving reps earn fewer bonuses.
      if (doubleAttrition) {
        cumTotalCost -= results.rows[i].bonuses * (1 - DOUBLE_ATTRITION_SURVIVAL);
      }
    }
    if (halfSales) {
      cumGrossRevenue = cumGrossRevenue * 0.5;
      cumGrossProfit = cumGrossProfit * 0.5;
    }
    if (doubleAttrition) {
      cumGrossRevenue = cumGrossRevenue * 0.5;
      cumGrossProfit = cumGrossProfit * 0.5;
    }
    // ROI = (gain − cost) ÷ cost
    const roi = cumTotalCost > 0 ? (cumGrossProfit - cumTotalCost) / cumTotalCost * 100 : 0;
    const totalNetProfit = cumGrossProfit - cumTotalCost;
    return {
      totalGrossRevenue: cumGrossRevenue,
      totalGrossProfit: cumGrossProfit,
      totalNetProfit,
      roi
    };
  }, [results.rows, halfSales, doubleAttrition, timeframe]);
  // Net team growth mirrors the projection table's Active Reps (Average) metric
  // for the final month shown: launched (0-30d at 50%) + 30d+ + 60d+ + 90d+.
  // The double attrition scenario halves every active-rep bucket, exactly as
  // the projection table does. Half sales doesn't change headcount.
  const teamScenarioMultiplier = doubleAttrition ? 0.5 : 1;
  // On a pest revenue trail the team stops at Month 12, so longer views
  // report the year-one team.
  const teamMonth = revenueTrail && timeframe > YEAR_ONE_MONTHS ? YEAR_ONE_MONTHS : timeframe;
  const netTeamGrowth = useMemo(() => {
    const lastRow = results.rows[teamMonth];
    if (!lastRow) return 0;
    return activeRepsForRow(lastRow) * teamScenarioMultiplier;
  }, [results.rows, teamMonth, teamScenarioMultiplier]);
  const canOptimizeTeam = !!onOptimizeForTeamTarget;
  const startEditingTeamTarget = () => {
    setTeamTargetValue(netTeamGrowth.toFixed(1));
    setEditingTeamTarget(true);
  };
  const commitTeamTarget = () => {
    const parsed = parseFloat(teamTargetValue);
    setEditingTeamTarget(false);
    if (!isNaN(parsed) && parsed > 0 && onOptimizeForTeamTarget) {
      // The optimizer works on unadjusted numbers, so scale the target back
      // up to land on the number typed while a scenario is active.
      onOptimizeForTeamTarget(parsed / teamScenarioMultiplier, timeframe);
    }
  };
  const handleTeamTargetKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      commitTeamTarget();
    } else if (e.key === 'Escape') {
      setEditingTeamTarget(false);
    }
  };
  const roiClass = calculatedTotals.roi >= 0 ? 'text-green-600' : 'text-red-600';
  const startEditingTarget = () => {
    setTargetValue(String(Math.round(calculatedTotals.totalGrossRevenue)));
    setEditingTarget(true);
  };
  const commitTarget = () => {
    const parsed = parseFloat(targetValue);
    setEditingTarget(false);
    if (!isNaN(parsed) && parsed > 0 && onOptimizeForTarget) {
      onOptimizeForTarget(parsed, timeframe);
    }
  };
  const handleTargetKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      commitTarget();
    } else if (e.key === 'Escape') {
      setEditingTarget(false);
    }
  };
  const canOptimize = !!onOptimizeForTarget;
  if (inputs?.industry === 'Summer Pest') {
    return <SummerPestHeadline headline={yearOneHeadline(results, inputs, {
      halfSales,
      doubleAttrition
    })} inputs={inputs} timeframe={timeframe} grossProfit={calculatedTotals.totalGrossProfit} contribution={calculatedTotals.totalNetProfit} roi={calculatedTotals.roi} scenarioActive={halfSales || doubleAttrition} data-id="element-1357" data-mp="3hnh6zdg1wj2y" />;
  }
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6" data-id="element-1358" data-mp="2yo38qjbagm6n">
      {/* Net Team Growth — active reps in the final month shown in projections */}
      <div className="p-4 md:p-6 rounded-xl bg-gray-50 border border-gray-200" data-id="element-1359" data-mp="38mrcqe6fv0rc">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-1360" data-mp="38kodd3jebv9y">
          {teamMonth}-Month Net Team Growth
        </div>
        {editingTeamTarget ? <div className="relative" data-id="element-1361" data-mp="nviiecrwl7xx">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg" data-id="element-1362" data-mp="nymtzv7p75ym">
              +
            </span>
            <input type="number" min="0" step="1" value={teamTargetValue} onChange={e => setTeamTargetValue(e.target.value)} onKeyDown={handleTeamTargetKeyDown} onBlur={commitTeamTarget} autoFocus className="w-full pl-6 pr-2 py-1 text-2xl md:text-3xl font-bold text-navy border-2 border-amber rounded outline-none bg-white" data-id="element-1363" data-mp="3pfqvhzvkstib" />
          </div> : <div onClick={canOptimizeTeam ? startEditingTeamTarget : undefined} className={`text-2xl md:text-3xl font-bold text-navy ${canOptimizeTeam ? 'cursor-pointer hover:text-amber transition-colors underline decoration-dotted decoration-gray-400 underline-offset-4' : ''}`} title={canOptimizeTeam ? 'Click to set a team-growth target — campaigns will auto-optimize' : undefined} data-id="element-1364" data-mp="3ldm5rc890h0y">
            +{netTeamGrowth.toFixed(1)}
          </div>}
        <div className="text-sm text-gray-600 mt-2 flex items-center gap-2" data-id="element-1365" data-mp="188qx6opu8oyc">
          {canOptimizeTeam ? <span data-id="element-1366" data-mp="tfx7kaguqsfy">Click to set a Month {teamMonth} rep target</span> : <span data-id="element-1367" data-mp="yerzc1wsrrc7">Active reps at Month {teamMonth}</span>}
          {canUndoOptimize && onUndoOptimize && <button type="button" onClick={onUndoOptimize} className="text-amber-600 hover:text-amber font-semibold underline underline-offset-2" data-id="element-1368" data-mp="1r5ia8gese7ty">
              Undo
            </button>}
        </div>
      </div>

      {/* Gross Revenue - click to set a target and auto-optimize campaigns */}
      <div className="p-4 md:p-6 rounded-xl bg-gray-50 border border-gray-200" data-id="element-1369" data-mp="1p0t55f58fqk">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-1370" data-mp="5t8goz9v7pn7">
          {timeframe}-Month Gross Revenue
          {(halfSales || doubleAttrition) && <span className="ml-2 text-xs text-orange-600 font-bold" data-id="element-1371" data-mp="sgnjqpncgpu4">
              (Scenario Active)
            </span>}
        </div>
        {editingTarget ? <div className="relative" data-id="element-1372" data-mp="oh6bxpkg3gls">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg" data-id="element-1373" data-mp="jgin9ajovbuq">
              $
            </span>
            <input type="number" min="0" step="10000" value={targetValue} onChange={e => setTargetValue(e.target.value)} onKeyDown={handleTargetKeyDown} onBlur={commitTarget} autoFocus className="w-full pl-6 pr-2 py-1 text-2xl md:text-3xl font-bold text-navy border-2 border-amber rounded outline-none bg-white" data-id="element-1374" data-mp="1g5qbwrflu12v" />
          </div> : <div onClick={canOptimize ? startEditingTarget : undefined} className={`text-2xl md:text-3xl font-bold text-navy ${canOptimize ? 'cursor-pointer hover:text-amber transition-colors underline decoration-dotted decoration-gray-400 underline-offset-4' : ''}`} title={canOptimize ? 'Click to set a target — campaigns will auto-optimize' : undefined} data-id="element-1375" data-mp="1v2h9cv5px2pu">
            {fmtMoney(calculatedTotals.totalGrossRevenue)}
          </div>}
        <div className="text-sm text-gray-600 mt-2 flex items-center gap-2" data-id="element-1376" data-mp="2lboqt4i0uuij">
          {canOptimize ? <span data-id="element-1377" data-mp="2v3ccspvt7jcm">Click to set a {timeframe}-mo target</span> : <span data-id="element-1378" data-mp="2v5fdq4yi2ww4">Cumulative top-line revenue</span>}
          {canUndoOptimize && onUndoOptimize && <button type="button" onClick={onUndoOptimize} className="text-amber-600 hover:text-amber font-semibold underline underline-offset-2" data-id="element-1379" data-mp="3048k7dfddnms">
              Undo
            </button>}
        </div>
      </div>

      {/* Gross Profit */}
      <div className="p-4 md:p-6 rounded-xl bg-gray-50 border border-gray-200" data-id="element-1380" data-mp="50qwo5bgd8wv">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-1381" data-mp="4ymvvw8uoq0t">
          {timeframe}-Month Gross Profit
          {(halfSales || doubleAttrition) && <span className="ml-2 text-xs text-orange-600 font-bold" data-id="element-1382" data-mp="erlso1ei3mvi">
              (Scenario Active)
            </span>}
        </div>
        <div className="text-2xl md:text-3xl font-bold text-green-600" data-id="element-1383" data-mp="iui5tcl9uqs2">
          {fmtMoney(calculatedTotals.totalGrossProfit)}
        </div>
        <div className="text-sm text-gray-600 mt-2" data-id="element-1384" data-mp="sq21pyzu5pgt">
          GP / Sale: {fmtMoney(results.gpPerSale)}
        </div>
      </div>

      {/* ROI */}
      <div className="p-4 md:p-6 rounded-xl bg-gray-50 border border-gray-200" data-id="element-1385" data-mp="xnbwzv9wovck">
        <div className="text-sm text-gray-600 mb-2 font-semibold" data-id="element-1386" data-mp="2uj9fh3zsvz0l">
          {timeframe}-Month Return on Recruiting Spend
          {(halfSales || doubleAttrition) && <span className="ml-2 text-xs text-orange-600 font-bold" data-id="element-1387" data-mp="1glx1d1f1rbsq">
              (Scenario Active)
            </span>}
        </div>
        <div className={`text-2xl md:text-3xl font-bold ${roiClass}`} data-id="element-1388" data-mp="1bsu3ih38lg6q">
          {roiMultiple(calculatedTotals.roi)}
        </div>
        <div className="text-sm text-gray-600 mt-2" data-id="element-1389" data-mp="12uifmmtr8whz">
          Contribution after recruiting costs:{' '}
          {fmtMoney(calculatedTotals.totalNetProfit)}
        </div>
      </div>
    </div>;
}